import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { heroContent } from '../data/mock';
import { getImageUrl } from '../lib/api';
import { ArrowRight, CheckCircle, ChevronLeft, ChevronRight } from 'lucide-react';

const carouselImages = [
  {
    src: 'hero_carousel_1.png',
    alt: 'Āra pakuskapji no metāla - pakuskapis.lv™ risinājumi'
  },
  {
    src: 'hero_carousel_2.png',
    alt: 'Iekštelpu pakuskapji ar koka apdari - pakuskapis.lv™'
  },
  {
    src: 'hero_carousel_3.png',
    alt: 'Pakuskapji ar stikla durvīm saulainā vidē - pakuskapis.lv™'
  }
];

const HeroSection = ({ onCtaClick }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + carouselImages.length) % carouselImages.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(nextSlide, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide]);

  const handleManualNav = (direction) => {
    setIsAutoPlaying(false);
    if (direction === 'next') nextSlide();
    else prevSlide();
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* Background Carousel */}
      <div className="absolute inset-0 z-0">
        {carouselImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={getImageUrl(image.src)}
              alt={image.alt}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        ))}
        
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/70 to-slate-900/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-slate-900/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40">
        <div className="max-w-3xl">
          <div className="space-y-8">
            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight tracking-tight drop-shadow-lg">
                pakuskapis.lv<sup className="text-xl lg:text-2xl align-super">™</sup> risinājumi reāliem lietošanas apstākļiem
              </h1>
              <p className="text-xl lg:text-2xl text-white/90 leading-relaxed max-w-2xl drop-shadow">
                Piemēroti viedie skapji pašvaldībām, bankām, uzņēmumiem un sabiedriskām iestādēm Latvijā.
              </p>
            </div>

            <div className="flex items-center space-x-2 text-white/80">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              <span className="text-sm lg:text-base font-medium">{heroContent.proofLine}</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                size="lg"
                onClick={onCtaClick}
                className="bg-white hover:bg-slate-100 text-slate-900 px-8 py-6 text-lg font-semibold group shadow-xl"
              >
                {heroContent.ctaText}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Carousel Navigation */}
      <div className="absolute z-20 bottom-8 left-1/2 -translate-x-1/2 flex items-center space-x-4">
        {/* Prev button */}
        <button
          onClick={() => handleManualNav('prev')}
          className="w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-all border border-white/30"
          aria-label="Iepriekšējais attēls"
        >
          <ChevronLeft className="w-5 h-5 text-white" />
        </button>

        {/* Dots */}
        <div className="flex space-x-2">
          {carouselImages.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentSlide(index);
                setIsAutoPlaying(false);
                setTimeout(() => setIsAutoPlaying(true), 10000);
              }}
              className={`h-2 rounded-full transition-all ${
                index === currentSlide 
                  ? 'bg-white w-8' 
                  : 'bg-white/40 hover:bg-white/60 w-2'
              }`}
              aria-label={`Attēls ${index + 1}`}
            />
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => handleManualNav('next')}
          className="w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center transition-all border border-white/30"
          aria-label="Nākamais attēls"
        >
          <ChevronRight className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 right-8 z-20 hidden lg:flex flex-col items-center space-y-2 text-white/60">
        <span className="text-xs uppercase tracking-widest rotate-90 origin-center translate-y-8">Scroll</span>
        <div className="w-px h-16 bg-gradient-to-b from-white/60 to-transparent" />
      </div>
    </section>
  );
};

export default HeroSection;
